import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { hasAuth0 } from '../env';
import { useDevTokenStore } from '../stores/devToken';
import { type ApiClient, createApiClient } from './client';

const ApiCtx = createContext<ApiClient | null>(null);

export function ApiProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  // Subscribe with a stable setter selector so the memo doesn't re-fire
  // on every store change.
  const setDevToken = useDevTokenStore((s) => s.setToken);
  const client = useMemo(
    () =>
      // Dev-refresh fallback is for dev-bypass mode only. With Auth0
      // configured, the SDK handles token refresh itself (via the
      // /oauth/token endpoint), so we skip the dev-mode path and its
      // 404 noise. exactOptionalPropertyTypes means we can't pass
      // `undefined` — conditionally spread instead.
      createApiClient({
        getToken,
        ...(hasAuth0() ? {} : { onTokenRefreshed: (t: string) => setDevToken(t) }),
      }),
    [getToken, setDevToken],
  );
  return <ApiCtx.Provider value={client}>{children}</ApiCtx.Provider>;
}

export function useApi(): ApiClient {
  const client = useContext(ApiCtx);
  if (!client) throw new Error('useApi must be used within <ApiProvider>');
  return client;
}
